'use client';

import { CLSMetric, FCPMetric, INPMetric, LCPMetric, TTFBMetric } from 'web-vitals';

// Web Vitals thresholds
const VITALS_THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  INP: { good: 200, poor: 500 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 },
} as const;

type VitalsMetric = CLSMetric | FCPMetric | INPMetric | LCPMetric | TTFBMetric;

function getVitalScore(metric: VitalsMetric): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = VITALS_THRESHOLDS[metric.name as keyof typeof VITALS_THRESHOLDS];
  if (!thresholds) return 'good';
  
  if (metric.value <= thresholds.good) return 'good';
  if (metric.value <= thresholds.poor) return 'needs-improvement';
  return 'poor';
}

export function sendToAnalytics(metric: VitalsMetric) {
  const score = getVitalScore(metric);
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.group(`🔍 Web Vitals: ${metric.name}`);
    console.log(`Value: ${metric.value.toFixed(2)}${metric.name === 'CLS' ? '' : 'ms'}`);
    console.log(`Score: ${score}`);
    console.log(`Rating: ${metric.rating}`);
    console.log(`Navigation Type: ${metric.navigationType}`);
    console.groupEnd();
  }

  // Send to analytics in production
  if (typeof window !== 'undefined' && 'gtag' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
      custom_map: {
        metric_id: metric.id,
        metric_value: metric.value,
        metric_delta: metric.delta,
        metric_rating: metric.rating,
        metric_score: score,
        metric_navigation_type: metric.navigationType,
      },
    });
  }

  // Send to Vercel Analytics if available
  if (typeof window !== 'undefined' && 'va' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).va('track', `Web Vitals: ${metric.name}`, {
      value: metric.value,
      rating: metric.rating,
      score,
      navigationType: metric.navigationType,
    });
  }
}

// Initialize Web Vitals monitoring
export async function initWebVitals() {
  if (typeof window === 'undefined') return;

  try {
    const webVitals = await import('web-vitals');
    
    if (webVitals.onCLS) webVitals.onCLS(sendToAnalytics);
    if (webVitals.onFCP) webVitals.onFCP(sendToAnalytics);
    if (webVitals.onINP) webVitals.onINP(sendToAnalytics);
    if (webVitals.onLCP) webVitals.onLCP(sendToAnalytics);
    if (webVitals.onTTFB) webVitals.onTTFB(sendToAnalytics);
  } catch (error) {
    console.warn('Failed to initialize Web Vitals:', error);
  }
}

// Web Vitals debug panel (development only)
export function createVitalsDebugPanel() {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return;

  let panel: HTMLDivElement | null = null;
  const vitalsData: Record<string, VitalsMetric> = {};

  function createPanel() {
    panel = document.createElement('div');
    panel.id = 'web-vitals-debug';
    panel.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 16px;
      border-radius: 8px;
      font-family: monospace;
      font-size: 12px;
      z-index: 10000;
      max-width: 300px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      cursor: move;
      user-select: none;
    `;

    // Add drag functionality
    let isDragging = false;
    let currentX = 0;
    let currentY = 0;
    let initialX = 0;
    let initialY = 0;

    panel.addEventListener('mousedown', (e) => {
      if (e.target === panel || (e.target as HTMLElement).tagName === 'DIV') {
        isDragging = true;
        initialX = e.clientX - currentX;
        initialY = e.clientY - currentY;
        panel!.style.cursor = 'grabbing';
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (isDragging && panel) {
        e.preventDefault();
        currentX = e.clientX - initialX;
        currentY = e.clientY - initialY;
        
        // Keep panel within viewport bounds
        const rect = panel.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;
        
        currentX = Math.max(0, Math.min(currentX, maxX));
        currentY = Math.max(0, Math.min(currentY, maxY));
        
        panel.style.transform = `translate(${currentX}px, ${currentY}px)`;
      }
    });

    document.addEventListener('mouseup', () => {
      if (isDragging && panel) {
        isDragging = false;
        panel.style.cursor = 'move';
      }
    });

    document.body.appendChild(panel);
    updatePanel();
  }

  function updatePanel() {
    if (!panel) return;
    
    const metrics = Object.values(vitalsData);
    panel.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px; color: #f97316;">Web Vitals</div>
      ${metrics.map(metric => {
        const score = getVitalScore(metric);
        const color = score === 'good' ? '#22c55e' : score === 'needs-improvement' ? '#eab308' : '#ef4444';
        return `
          <div style="margin-bottom: 4px;">
            <span style="color: ${color};">●</span>
            <strong>${metric.name}:</strong> 
            ${metric.value.toFixed(metric.name === 'CLS' ? 3 : 0)}${metric.name === 'CLS' ? '' : 'ms'}
            <span style="color: #888; font-size: 10px;">(${score})</span>
          </div>
        `;
      }).join('')}
    `;
  }

  // Store metrics and update panel
  async function debugSendToAnalytics(metric: VitalsMetric) {
    vitalsData[metric.name] = metric;
    if (!panel) createPanel();
    updatePanel();
    sendToAnalytics(metric);
  }

  return debugSendToAnalytics;
}